import { Box, styled, IconButton, Stack } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useState, useEffect } from "react";
import ReactPlayer from "react-player";

interface VideoPlayerProps {
  src: string;
  coverImage?: string;
  controls?: boolean;
  loop?: boolean;
  maxHeight?: string;
  maxWidth?: string;
}

const PlayerContainer = styled(Box)<{ maxHeight?: string; maxWidth?: string }>(
  ({ theme, maxHeight, maxWidth }) => ({
    position: "relative",
    width: "100%",
    height: "100%",
    maxHeight: maxHeight || "100%",
    maxWidth: maxWidth || "100%",
    overflow: "hidden",
    backgroundColor: theme.palette.grey[100],
    aspectRatio: "16/9",
    "& video, & img": {
      objectFit: "cover",
      width: "100%",
      height: "100%",
    },
    "& .react-player": {
      position: "absolute",
      top: 0,
      left: 0,
    },
  })
);

const PlayOverlay = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.3)",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  zIndex: 2,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
  },
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  color: theme.palette.primary.main,
  width: 80,
  height: 80,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
    transform: "scale(1.1)",
  },
  transition: "all 0.2s ease",
}));

export default function VideoPlayer({
  src,
  coverImage,
  controls = true,
  loop = false,
  maxHeight,
  maxWidth,
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  // Reset playing state when video source changes
  useEffect(() => {
    setIsPlaying(false);
  }, [src]);

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        maxHeight: maxHeight || "100%",
        maxWidth: maxWidth || "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PlayerContainer maxHeight={maxHeight} maxWidth={maxWidth}>
        {/* Cover Image Thumbnail */}
        {coverImage && !isPlaying && (
          <Stack
            component="img"
            src={coverImage}
            alt="Video thumbnail"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              zIndex: 1,
              display: "block",
            }}
          />
        )}

        {/* React Player - Hidden when not playing */}
        <ReactPlayer
          src={src}
          width="100%"
          height="100%"
          controls={isPlaying && controls}
          loop={loop}
          playing={isPlaying}
          onPause={handlePause}
          onEnded={handleEnded}
          className="react-player"
          style={{
            objectFit: "cover",
            display: isPlaying ? "block" : "none",
          }}
        />

        {/* Play Button Overlay */}
        {!isPlaying && (
          <PlayOverlay onClick={handlePlayClick}>
            <PlayButton size="large">
              <PlayArrowIcon sx={{ fontSize: 40 }} />
            </PlayButton>
          </PlayOverlay>
        )}
      </PlayerContainer>
    </Stack>
  );
}
